import { Mesh } from "three";
import { loadLevelCommand } from "./LevelUtils";
import { getUserDataProperties } from "../Features/FeatureUtils";
import { AnimationsControllerContextType } from "../Features/Animation/AnimationsController";
import { LevelContextType } from "./LevelController";
import {
  initializeAnimationsCommand,
  playAnimationsCommand,
} from "./AnimationUtils";

export type CommandContext = {
  animationContext: AnimationsControllerContextType;
  levelContext: LevelContextType;
};

export type InitializationCommand = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext
) => (() => void) | void;

export type Command = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext
) => void;

export type InitializationCommandExecutor = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext,
  directCommandData?: boolean
) => ((() => void) | void)[];

export type CommandExecutor = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext,
  directCommandData?: boolean
) => void;

export const INITIALIZATION_COMMAND_MAP: Record<string, InitializationCommand> =
  {
    Animations: initializeAnimationsCommand,
  };

export const COMMAND_MAP_BASE = {
  LoadLevel: loadLevelCommand,
  Animations: playAnimationsCommand,
};

export const COMMAND_MAP: Record<string, Command> = COMMAND_MAP_BASE as any;

export type CommandConfig = Record<keyof typeof COMMAND_MAP_BASE, any>;

const executeCommandsInternal = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext,
  commandMap: Record<string, Command | InitializationCommand>,
  directCommandData?: boolean
): any[] => {
  const userDataCommandMap = directCommandData
    ? userData
    : (getUserDataProperties(userData, "Command") as Record<string, any>);
  const output = Object.keys(userDataCommandMap).map((commandName) => {
    const command = commandMap[commandName];

    if (command) {
      return command(mesh, userDataCommandMap, commandContext);
    }
  });

  return output.filter((value) => value !== undefined);
};

export const executeInitializationCommands: InitializationCommandExecutor = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext,
  directCommandData?: boolean
) =>
  executeCommandsInternal(
    mesh,
    userData,
    commandContext,
    INITIALIZATION_COMMAND_MAP,
    directCommandData
  );

export const executeCommands: CommandExecutor = (
  mesh: Mesh,
  userData: Record<string, any>,
  commandContext: CommandContext,
  directCommandData?: boolean
) => {
  executeCommandsInternal(
    mesh,
    userData,
    commandContext,
    COMMAND_MAP,
    directCommandData
  );
};
